/**
 * API client using axios
 */
import axios from 'axios'
import Session from './session'
import router from '../router'
import store from '../store'
import EmbeddedLocalStorage from '@/api/embedded'

/**
 * Returns base URL
 */
function baseURL () {
  return import.meta.env.NODE_ENV === 'production'
    ? window.runtimeConfig?.API_URL
    : import.meta.env?.VITE_API_URL
}

/**
 * Returns base URL with given path
 */
function baseURLPath (path) {
  const url = new URL(baseURL())
  url.pathname = path
  return url.toString()
}

/**
 * Concatenates URL parts
 */
function urlPath () {
  let path = ''
  for (const p of arguments) {
    if (typeof p === 'string') {
      path = path.concat('/', p.replace(/^\//, '').replace(/\/$/, ''))
    }
  }
  return path
}

/**
 * Returns API URL with given path
 * @param {String} path
 * @param {Boolean} if true, prepend path vith version (/v1/)
 */
function url (path, useVer = true) {
  let url = new URL(baseURL())
  if (useVer) {
    url = new URL(urlPath(url.pathname, version()), url)
  }
  if (path) {
    url = new URL(urlPath(url.pathname, path), url)
  }
  return url.href
}

/**
 * Check URL configuration
 */
function isReady () {
  try {
    const apiUrl = new URL(url())
    return /^https?/.test(apiUrl.protocol)
  } catch {
    return false
  }
}

/**
 * Returns API version
 */
function version () {
  return import.meta.env.NODE_ENV === 'production'
    ? window.runtimeConfig?.API_VER
    : import.meta.env.VITE_API_VER
}

// using a promise variable to avoid concurrent login redirects
// which can happen when a page requires multiple API calls
let loginRedirectPromise = null

/**
 * Redirects to the login page
 */
async function loginRedirect () {
  if (loginRedirectPromise) {
    return loginRedirectPromise
  }

  loginRedirectPromise = (async () => {
    await store.dispatch('authReset')
    const urlParams = new URLSearchParams(window.location.search)
    const embedded = urlParams.get('embedded') === 'true' || EmbeddedLocalStorage.get()

    if (embedded) {
      // prefer stuff on url over localStorage
      let embeddedData = {}
      if (urlParams.get('embedded') === 'true') {
        embeddedData = {
          cloudId: urlParams.get('cloud_id'),
          cloudUsername: urlParams.get('cloud_username'),
          cloudPassword: urlParams.get('cloud_password'),
          language: urlParams.get('language')
        }
      } else {
        embeddedData = EmbeddedLocalStorage.getData()
      }
      const query = {
        embedded,
        redirect: window.location.pathname.replace(/^\/+/, ''),
        cloud_id: embeddedData.cloudId,
        cloud_username: embeddedData.cloudUsername,
        cloud_password: embeddedData.cloudPassword,
        language: embeddedData.language
      }
      await router.push({ name: 'embedded_login', query })
    } else {
      // prevent infinite redirect loop by blacklisting the callback route
      let redirectPath = router.currentRoute.fullPath
      if (redirectPath.startsWith('/clusterpbx_callback')) {
        redirectPath = '/'
      }
      const newURL = `${baseURLPath('signin')}?redirect=${encodeURIComponent(redirectPath)}`
      window.location.replace(newURL)
    }

    loginRedirectPromise = null
  })()

  return loginRedirectPromise
}

// using a promise variable to avoid concurrent token refresh requests
// which can happen when a page requires multiple API calls
let refreshTokenPromise = null

/**
 * Refreshes auth tokens
 */
function refreshAccessToken () {
  refreshTokenPromise = axios({
    method: 'post',
    url: url('oauth/token', false),
    data: {
      grant_type: 'refresh_token',
      refresh_token: Session.refreshToken(),
      ...Session.clientID() ? { client_id: Session.clientID() } : {}
    }
  }).then(response => {
    Session.store(response.data)
  }).finally(() => {
    refreshTokenPromise = null
  })
  return refreshTokenPromise
}

/**
 * Refreshes auth tokens and retries the request
 */
async function refreshTokenAndRetry (config) {
  try {
    if (refreshTokenPromise) {
      await refreshTokenPromise
    } else {
      await refreshAccessToken()
    }

    config.headers.Authorization = Session.authHeader()
    return axios(config)
  } catch (error) {
    await loginRedirect()
    throw new Error('Failed to refresh token')
  }
}

/**
 * Refreshes auth tokens or ask for a new one
 * Independant from any requests
 * Used to init the application
 */
async function refreshOrLogin () {
  if (!Session.hasAuthMaterial()) {
    // Exception: do not redirect if on /signout route
    if (router.currentRoute && router.currentRoute.path === '/signout') {
      return
    }
    await loginRedirect()
  } else if (Session.isExpired()) {
    await refreshAccessToken()
  }
}

/**
 * Generic API request method
 */
function request (method, url, data = undefined, conf = {}) {
  const config = Object.assign({
    method,
    url,
    data,
    headers: {}
  }, conf)

  config.headers.Authorization = Session.authHeader()

  if (!Session.accessToken()) {
    // Exception: do not redirect if on /signout route
    if (router.currentRoute && router.currentRoute.path === '/signout') {
      throw new Error('No access token (signout route)')
    }
    loginRedirect()
    throw new Error('No access token')
  }

  if (Session.isExpired()) {
    return refreshTokenAndRetry(config)
  }

  return new Promise((resolve, reject) => {
    axios(config)
      .then(resolve)
      .catch(async (error) => {
        if (error.response && error.response.status === 401) {
          if (Session.refreshToken()) {
            refreshTokenAndRetry(config).then(resolve).catch(reject)
          } else {
            await loginRedirect()
            reject(new Error('No refresh token'))
          }
        } else {
          reject(error)
        }
      })
  })
}

/**
 * API request methods
 */
function GET (url, config = {}) {
  return request('get', url, undefined, config)
}

function DELETE (url) {
  return request('delete', url)
}

function POST (url, data, config = {}) {
  return request('post', url, data, config)
}

function PUT (url, data) {
  return request('put', url, data)
}

function PATCH (url, data) {
  return request('patch', url, data)
}

export default {
  url,
  baseURL,
  baseURLPath,
  isReady,
  version,
  request,
  refreshTokenAndRetry,
  refreshAccessToken,
  loginRedirect,
  refreshOrLogin,
  GET,
  DELETE,
  POST,
  PUT,
  PATCH
}
